import { useState, useEffect, useCallback, useContext } from "react";
import { ChatSettingsType, TempSettingsType } from "../types";
import { UrlParamsContext } from "../../../app/context/url-params-context";
import { upsertSettings } from "../settings-service";
import { transformChatSettingsToSettingsArray } from "../utils";

export const useChatSettingsManager = (
  chatSettings: ChatSettingsType,
  setChatSettings: React.Dispatch<React.SetStateAction<ChatSettingsType>>
) => {
  const { functionKey, username } = useContext(UrlParamsContext);
  const [tempSettings, setTempSettings] = useState<TempSettingsType>({});
  const [settingsChanged, setSettingsChanged] = useState(false);

  useEffect(() => {
    // Initialize tempSettings from chatSettings
    const initialSettings: TempSettingsType = {};
    for (const key in chatSettings) {
      initialSettings[key] = chatSettings[key].selected_option as
        | string
        | boolean;
    }
    setTempSettings(initialSettings);
  }, [chatSettings]);

  useEffect(() => {
    const changed = Object.entries(chatSettings).some(
      ([key, setting]) => tempSettings[key] !== setting.selected_option
    );
    setSettingsChanged(changed);
  }, [tempSettings, chatSettings]);

  const handleChange = useCallback((key: string, value: string | boolean) => {
    setTempSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveSettings = async () => {
    const newSettings = { ...chatSettings };
    for (const key in tempSettings) {
      newSettings[key] = {
        ...newSettings[key],
        selected_option: tempSettings[key],
      };
    }
    await upsertSettings(
      functionKey,
      username,
      transformChatSettingsToSettingsArray(newSettings)
    );
    setChatSettings(newSettings);
  };

  const resetTempSettings = () => {
    const reset: TempSettingsType = {};
    for (const key in chatSettings) {
      reset[key] = chatSettings[key].selected_option as string | boolean;
    }
    setTempSettings(reset);
  };
  return {
    tempSettings,
    handleChange,
    settingsChanged,
    resetTempSettings,
    saveSettings,
  };
};
